export const shouldShowReceiptForStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  return ["accepted", "startduty", "endduty", "completed"].includes(normalized);
};

const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  return String(value || "").trim().toLowerCase() === "true";
};

const normalizeObjectId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return String(value?._id || value?.id || value?.$oid || "");
  }
  return "";
};

const isNurseRegularVisit = (visit) =>
  String(visit?.specialistInfo?.role || "").trim().toLowerCase() === "nurse" &&
  String(visit?.dutyType || "").trim().toLowerCase() === "regular";

const isDailyQrVisit = (visit) =>
  String(visit?.paymentMode || "").trim().toLowerCase() === "daily_qr";

const isPhysioAssessmentVisit = (visit) =>
  String(visit?.specialistInfo?.role || "").trim().toLowerCase() === "physiotherapist" &&
  normalizeBoolean(visit?.isAssessment);

const getPhysioAssessmentAmount = (visit) =>
  toNumber(visit?.paymentInfo?.assessment?.fee);

const getPhysioSessionAmount = (session) =>
  toNumber(session?.extensionFees ?? session?.grandTotal);

const getPhysioSessionQty = (session) =>
  Math.max(1, toNumber(session?.extendedDays || 1));

const getPhysioSessionRate = (session) => {
  const qty = getPhysioSessionQty(session);
  const amount = getPhysioSessionAmount(session);
  return toNumber(session?.feesPerSession || (qty > 0 ? amount / qty : amount));
};

const hasPhysioAssessmentReceipt = (visit) =>
  Boolean(
    String(visit?.paymentInfo?.assessment?.paidInfo?.metadata?.razorpay_payment_id || "").trim(),
  );

const hasPhysioSessionReceipt = (session) =>
  Boolean(
    session?.paidInfo?.paid === true &&
    String(session?.paidInfo?.metadata?.razorpay_payment_id || "").trim(),
  );

const hasDailyDutyReceipt = (duty) =>
  String(duty?.payment?.status || "").trim().toLowerCase() === "paid" &&
  String(duty?.payment?.paymentID || "").trim();

const getDailyDutyAmount = (visit, duty) => {
  const dutyAmount = toNumber(duty?.payment?.amount);
  if (dutyAmount > 0) return dutyAmount;
  return toNumber(visit?.paymentInfo?.feePerDay);
};

const buildDailyDutyReceiptSections = (visit) => {
  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  return duties
    .map((duty, index) => ({ duty, index }))
    .filter(({ duty }) => hasDailyDutyReceipt(duty))
    .map(({ duty, index }) => {
      const dayNumber = duty?.day || index + 1;
      const dutyDate = String(duty?.date || "").trim();
      const amount = getDailyDutyAmount(visit, duty);
      const dateLabel = dutyDate ? ` - ${dutyDate}` : "";

      return {
        key: `daily-${dutyDate || index}`,
        title: `Day ${dayNumber} Receipt`,
        selectorLabel: `Day ${dayNumber}${dateLabel}`,
        fileLabel: `day-${dayNumber}-receipt`,
        paymentSegment: "daily",
        dutyDate,
        lineItems: [
          {
            particulars: `Home Care Charges - Day ${dayNumber}`,
            qty: 1,
            rate: amount,
            amount,
          },
        ],
        totalAmount: amount,
      };
    });
};

const findMatchingAssessmentForSession = (visit, session) => {
  const assessments = Array.isArray(visit?.assessment) ? visit.assessment : [];
  const assessmentId = normalizeObjectId(session?.assessmentID);

  if (assessmentId) {
    const matchedAssessment = assessments.find(
      (assessment) => normalizeObjectId(assessment?._id) === assessmentId,
    );
    if (matchedAssessment) return matchedAssessment;
  }

  return assessments.find((assessment) => assessment?.completed) || assessments[0] || null;
};

const formatTreatmentType = (value) => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
    if (items.length > 0) return items.join(", ");
  }

  const label = String(value || "").trim();
  return label || "";
};

export const getPhysioSessionLabel = (visit, session, index = 0) => {
  const assessment = findMatchingAssessmentForSession(visit, session);
  const treatmentType = formatTreatmentType(assessment?.treatmentPlan?.treatmentType);
  return treatmentType || `Session Extension ${index + 1}`;
};

const buildNurseRegularReceiptSection = (visit) => {
  const qty = Math.max(1, toNumber(visit?.paymentInfo?.totalDays || visit?.visitInfo?.days || 0));
  const rate = toNumber(visit?.paymentInfo?.feePerDay);
  const amount = toNumber(visit?.paymentInfo?.totalFees || qty * rate);

  return {
    key: "default",
    title: "Receipt",
    selectorLabel: "Receipt",
    fileLabel: "receipt",
    paymentSegment: "default",
    lineItems: [
      {
        particulars: "HOME CARE CHARGES",
        qty,
        rate,
        amount,
      },
    ],
    totalAmount: amount,
  };
};

const buildDefaultReceiptSection = (visit) => {
  const serviceFees = Array.isArray(visit?.paymentInfo?.serviceFees) ? visit.paymentInfo.serviceFees : [];
  const qty = Math.max(1, toNumber(visit?.paymentInfo?.totalDays || visit?.visitInfo?.days || 0));
  const lineItems = serviceFees.map((item, index) => {
    const rate = toNumber(item?.fee ?? item?.fees);
    return {
      particulars: String(item?.service || item?.name || `Service ${index + 1}`).trim() || `Service ${index + 1}`,
      qty,
      rate,
      amount: rate * qty,
    };
  });

  const totalFees = toNumber(visit?.paymentInfo?.totalFees);
  const totalAmount =
    totalFees > 0
      ? totalFees
      : lineItems.reduce((sum, item) => sum + toNumber(item?.amount), 0);

  return {
    key: "default",
    title: "Receipt",
    selectorLabel: "Receipt",
    fileLabel: "receipt",
    paymentSegment: "default",
    lineItems,
    totalAmount,
  };
};

export const buildReceiptSections = (visit) => {
  if (!visit || typeof visit !== "object") {
    return [];
  }

  if (isDailyQrVisit(visit)) {
    return buildDailyDutyReceiptSections(visit);
  }

  if (isNurseRegularVisit(visit)) {
    return [buildNurseRegularReceiptSection(visit)];
  }

  if (isPhysioAssessmentVisit(visit)) {
    const sections = [];
    const assessmentAmount = getPhysioAssessmentAmount(visit);

    if (assessmentAmount > 0 && hasPhysioAssessmentReceipt(visit)) {
      sections.push({
        key: "assessment",
        title: "Assessment Receipt",
        selectorLabel: "Assessment",
        fileLabel: "assessment-receipt",
        paymentSegment: "assessment",
        lineItems: [
          {
            particulars: "Assessment Fee",
            qty: 1,
            rate: assessmentAmount,
            amount: assessmentAmount,
          },
        ],
        totalAmount: assessmentAmount,
      });
    }

    const sessions = Array.isArray(visit?.paymentInfo?.sessions) ? visit.paymentInfo.sessions : [];
    sessions.forEach((session, index) => {
      const amount = getPhysioSessionAmount(session);
      if (amount <= 0 || !hasPhysioSessionReceipt(session)) return;

      const qty = getPhysioSessionQty(session);
      const rate = getPhysioSessionRate(session);
      const particulars = getPhysioSessionLabel(visit, session, index);

      sections.push({
        key: `session-${index}`,
        title: `${particulars} Receipt`,
        selectorLabel: particulars,
        fileLabel: particulars,
        paymentSegment: "session",
        sessionIndex: index,
        lineItems: [
          {
            particulars,
            qty,
            rate,
            amount,
          },
        ],
        totalAmount: amount,
      });
    });

    return sections;
  }

  return [buildDefaultReceiptSection(visit)];
};

export const buildReceiptLineItems = (visit, selection = {}) => {
  const receiptSections = buildReceiptSections(visit);

  if (isDailyQrVisit(visit)) {
    if (selection?.paymentSegment === "daily" && selection?.dutyDate) {
      return (
        receiptSections.find(
          (section) =>
            section.paymentSegment === "daily" &&
            section.dutyDate === selection.dutyDate,
        )?.lineItems || []
      );
    }

    return receiptSections.flatMap((section) => section.lineItems || []);
  }

  if (isPhysioAssessmentVisit(visit)) {
    if (selection?.paymentSegment === "assessment") {
      return receiptSections.find((section) => section.key === "assessment")?.lineItems || [];
    }

    if (
      selection?.paymentSegment === "session" &&
      Number.isInteger(selection?.sessionIndex)
    ) {
      return (
        receiptSections.find(
          (section) =>
            section.paymentSegment === "session" &&
            section.sessionIndex === selection.sessionIndex,
        )?.lineItems || []
      );
    }

    return receiptSections.flatMap((section) => section.lineItems || []);
  }

  return receiptSections[0]?.lineItems || [];
};

export const getReceiptTotalAmount = (visit, selection = {}) => {
  const receiptSections = buildReceiptSections(visit);

  if (isDailyQrVisit(visit)) {
    if (selection?.paymentSegment === "daily" && selection?.dutyDate) {
      return (
        receiptSections.find(
          (section) =>
            section.paymentSegment === "daily" &&
            section.dutyDate === selection.dutyDate,
        )?.totalAmount || 0
      );
    }

    return receiptSections.reduce((sum, section) => sum + toNumber(section?.totalAmount), 0);
  }

  if (isPhysioAssessmentVisit(visit)) {
    if (selection?.paymentSegment === "assessment") {
      return receiptSections.find((section) => section.key === "assessment")?.totalAmount || 0;
    }

    if (
      selection?.paymentSegment === "session" &&
      Number.isInteger(selection?.sessionIndex)
    ) {
      return (
        receiptSections.find(
          (section) =>
            section.paymentSegment === "session" &&
            section.sessionIndex === selection.sessionIndex,
        )?.totalAmount || 0
      );
    }

    return receiptSections.reduce((sum, section) => sum + toNumber(section?.totalAmount), 0);
  }

  return receiptSections[0]?.totalAmount || 0;
};

const smallNumberWords = [
  "",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const tensWords = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

const convertBelowThousand = (value) => {
  const num = toNumber(value);
  if (num === 0) return "";
  if (num < 20) return smallNumberWords[num];
  if (num < 100) {
    const tens = Math.floor(num / 10);
    const rest = num % 10;
    return `${tensWords[tens]}${rest ? ` ${smallNumberWords[rest]}` : ""}`.trim();
  }
  const hundreds = Math.floor(num / 100);
  const rest = num % 100;
  return `${smallNumberWords[hundreds]} hundred${rest ? ` ${convertBelowThousand(rest)}` : ""}`.trim();
};

export const numberToIndianCurrencyWords = (value) => {
  const amount = Math.round(toNumber(value));
  if (amount === 0) return "RUPEES ZERO ONLY";

  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const remainder = amount % 1000;
  const segments = [];

  if (crore) segments.push(`${convertBelowThousand(crore)} crore`);
  if (lakh) segments.push(`${convertBelowThousand(lakh)} lakh`);
  if (thousand) segments.push(`${convertBelowThousand(thousand)} thousand`);
  if (remainder) segments.push(convertBelowThousand(remainder));

  return `RUPEES ${segments.join(" ").trim().toUpperCase()} ONLY`;
};
